<template>
  <section class="pwa-section" id="pwa">
    <div class="container">
      <div class="section-content">
        <div class="section-tag">Progressive Web App</div>
        <h2 class="section-title">
          Install <span class="highlight">Sherd</span> as an App
        </h2>
        <p class="section-subtitle">Get the full app experience directly from your browser. No app store required!</p>
      </div>
      
      <div class="pwa-content">
        <div class="pwa-tabs">
          <button 
            class="tab-button" 
            :class="{ active: activeTab === 'android' }"
            @click="activeTab = 'android'"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M17.5 15.5C18.33 15.5 19 14.83 19 14C19 13.17 18.33 12.5 17.5 12.5C16.67 12.5 16 13.17 16 14C16 14.83 16.67 15.5 17.5 15.5Z" fill="currentColor"/>
              <path d="M6.5 15.5C7.33 15.5 8 14.83 8 14C8 13.17 7.33 12.5 6.5 12.5C5.67 12.5 5 13.17 5 14C5 14.83 5.67 15.5 6.5 15.5Z" fill="currentColor"/>
              <path d="M7.01 10.5H16.99C17.01 10.34 17.01 10.17 16.99 10.01L15.28 4.63C15.22 4.25 14.86 4 14.47 4H9.53C9.14 4 8.78 4.25 8.72 4.63L7.01 10.01C6.99 10.17 6.99 10.34 7.01 10.5Z" fill="currentColor"/>
              <path d="M7 11V20C7 20.55 7.45 21 8 21H16C16.55 21 17 20.55 17 20V11H7Z" fill="currentColor"/>
              <circle cx="9" cy="7" r="0.5" fill="white"/>
              <circle cx="15" cy="7" r="0.5" fill="white"/>
            </svg>
            Android
          </button>
          <button 
            class="tab-button" 
            :class="{ active: activeTab === 'ios' }"
            @click="activeTab = 'ios'"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 21.99 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 21.99C7.78997 22.03 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.54 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" fill="currentColor"/>
            </svg>
            iOS
          </button>
        </div>

        <div class="pwa-tab-content">
          <!-- Android Tab -->
          <div v-if="activeTab === 'android'" class="tab-content">
            <div class="content-layout">
              <div class="mockup-container">
                <div class="phone-mockup">
                  <div class="phone-frame">
                    <div class="phone-screen">
                      <video 
                        autoplay 
                        muted 
                        loop 
                        playsinline
                        class="phone-video"
                        poster="/phone-screenshot.png"
                      >
                        <source src="/images/sherd_android.mp4" type="video/mp4">
                        <img src="/phone-screenshot.png" alt="Sherd App Interface on Android" class="phone-screenshot">
                      </video>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="instructions-container">
                <h3>Install on Android</h3>
                <p class="instructions-intro">Follow these simple steps to install Sherd as a native app on your Android device:</p>
                
                <div class="instruction-steps">
                  <div class="step">
                    <div class="step-header">
                      <div class="step-number">1</div>
                      <h4>Open Chrome Browser</h4>
                    </div>
                    <div class="step-content">
                      <p>Launch <strong>Google Chrome</strong> and navigate to <span class="url-highlight">play.sherdapp.com</span></p>
                    </div>
                  </div>
                  
                  <div class="step">
                    <div class="step-header">
                      <div class="step-number">2</div>
                      <h4>Access Browser Menu</h4>
                    </div>
                    <div class="step-content">
                      <p>Tap the <strong>three vertical dots</strong> <span class="icon-highlight">⋮</span> in the top-right corner of your browser</p>
                    </div>
                  </div>
                  
                  <div class="step">
                    <div class="step-header">
                      <div class="step-number">3</div>
                      <h4>Select Install Option</h4>
                    </div>
                    <div class="step-content">
                      <p>Look for <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong> in the dropdown menu</p>
                    </div>
                  </div>
                  
                  <div class="step">
                    <div class="step-header">
                      <div class="step-number">4</div>
                      <h4>Complete Installation</h4>
                    </div>
                    <div class="step-content">
                      <p>Tap <strong>"Add"</strong> or <strong>"Install"</strong> to place Sherd on your home screen</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- iOS Tab -->
          <div v-if="activeTab === 'ios'" class="tab-content">
            <div class="content-layout">
              <div class="mockup-container">
                <div class="phone-mockup">
                  <div class="phone-frame">
                    <div class="phone-screen">
                      <video 
                        autoplay 
                        muted 
                        loop 
                        playsinline
                        class="phone-video"
                        poster="/phone-screenshot.png"
                      >
                        <source src="/images/sherd_ios.mp4" type="video/mp4">
                        <img src="/phone-screenshot.png" alt="Sherd App Interface on iOS" class="phone-screenshot">
                      </video>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="instructions-container">
                <h3>Install on iOS</h3>
                <p class="instructions-intro">Add Sherd to your iPhone or iPad home screen in just a few taps:</p>
                
                <div class="instruction-steps">
                  <div class="step">
                    <div class="step-header">
                      <div class="step-number">1</div>
                      <h4>Open Chrome or Safari Browser</h4>
                    </div>
                    <div class="step-content">
                      <p>Launch <strong>Chrome or Safari</strong> and go to <span class="url-highlight">play.sherdapp.com</span></p>
                    </div>
                  </div>
                  
                  <div class="step">
                    <div class="step-header">
                      <div class="step-number">2</div>
                      <h4>Tap Share Button</h4>
                    </div>
                    <div class="step-content">
                      <p>Tap the <strong>share icon</strong> <img src="/images/pwa/share-icon.svg" alt="iOS Share Icon" class="ios-share-icon"> at the bottom center of your screen</p>
                    </div>
                  </div>
                  
                  <div class="step">
                    <div class="step-header">
                      <div class="step-number">3</div>
                      <h4>Find Add to Home Screen</h4>
                    </div>
                    <div class="step-content">
                      <p>Scroll down in the share menu and tap <strong>"Add to Home Screen"</strong></p>
                    </div>
                  </div>
                  
                  <div class="step">
                    <div class="step-header">
                      <div class="step-number">4</div>
                      <h4>Confirm Installation</h4>
                    </div>
                    <div class="step-content">
                      <p>Tap <strong>"Add"</strong> in the top-right corner to install Sherd</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- PWA Benefits Section -->
        <div class="pwa-benefits-section">
          <div class="benefits-header">
            <h3>Why Install the App?</h3>
            <p>Experience Sherd like never before with these exclusive benefits</p>
          </div>
          
          <div class="benefits-grid">
            <div class="benefit-card">
              <div class="benefit-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h4>Lightning Fast</h4>
              <p>Instant loading and smooth performance optimized for mobile exploration</p>
            </div>
            
            <div class="benefit-card">
              <div class="benefit-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M17 2H7C5.9 2 5 2.9 5 4V20C5 21.1 5.9 22 7 22H17C18.1 22 19 21.1 19 20V4C19 2.9 18.1 2 17 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M9 10H15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M9 14H15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h4>Native App Feel</h4>
              <p>Full-screen experience with native navigation and app-like interactions</p>
            </div>
            
            <div class="benefit-card">
              <div class="benefit-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M18 8A6 6 0 0 0 6 8C6 7 6 6 6 6S6 7 6 8A6 6 0 1 0 18 8Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M13 8H13.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M13 16H13.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h4>Smart Notifications</h4>
              <p>Get notified when you're near historical sites and new adventures are available</p>
            </div>
            
            <div class="benefit-card">
              <div class="benefit-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
                  <path d="M12 1V3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M12 21V23" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M4.22 4.22L5.64 5.64" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M18.36 18.36L19.78 19.78" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M1 12H3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M21 12H23" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M4.22 19.78L5.64 18.36" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M18.36 5.64L19.78 4.22" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h4>Works Offline</h4>
              <p>Continue your adventures even without internet connection in remote historical sites</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'

const activeTab = ref<'android' | 'ios'>('android')
</script>

<style scoped>
.pwa-section {
  padding: 120px 0;
  background: #fafafa;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.section-content {
  text-align: center;
  margin-bottom: 80px;
}

.section-tag {
  display: inline-block;
  background: #f8f9fa;
  color: #666;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 30px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-title {
  font-size: 2.8rem;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 30px;
  line-height: 1.2;
}

.highlight {
  background: linear-gradient(135deg, var(--ion-color-primary), var(--ion-color-primary-shade));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #666;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
}

.pwa-content {
  padding: 50px 0;
}

.pwa-tabs {
  display: flex;
  justify-content: center;
  margin-bottom: 50px;
  background: #f8f9fa;
  border-radius: 15px;
  padding: 8px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 50px;
}

.tab-button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  background: transparent;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-button.active {
  background: white;
  color: var(--ion-color-primary);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.tab-button svg {
  width: 20px;
  height: 20px;
}

.content-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
}

.mockup-container {
  display: flex;
  justify-content: center;
}

/* Hero-style phone mockup */
.phone-mockup {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.phone-frame {
  width: 320px;
  height: 640px;
  background: #000;
  border-radius: 30px;
  padding: 8px;
  box-shadow: 
    0 0 0 2px #333,
    0 20px 60px rgba(0, 0, 0, 0.3),
    inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  position: relative;
}

.phone-frame::before {
  content: '';
  position: absolute;
  top: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 4px;
  background: #333;
  border-radius: 2px;
}

.phone-frame::after {
  content: '';
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 140px;
  height: 4px;
  background: #333;
  border-radius: 2px;
}

.phone-screen {
  width: 100%;
  height: 100%;
  border-radius: 22px;
  overflow: hidden;
  background: var(--ion-background-color);
}

.phone-video,
.phone-screenshot {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.instructions-container h3 {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 15px;
}

.instructions-intro {
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 40px;
  line-height: 1.6;
}

.instruction-steps {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.step {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 20px;
}

.step-number {
  width: 45px;
  height: 45px;
  background: linear-gradient(135deg, var(--ion-color-primary), var(--ion-color-primary-shade));
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.2rem;
  flex-shrink: 0;
  box-shadow: 0 5px 15px rgba(235, 87, 99, 0.3);
}

.step-header h4 {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.step-content {
  margin-left: 65px;
}

.step-content p {
  color: #666;
  line-height: 1.6;
  margin: 0;
  font-size: 1rem;
}

.url-highlight {
  background: linear-gradient(135deg, var(--ion-color-primary), var(--ion-color-primary-shade));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 600;
}

.icon-highlight {
  background: #f8f9fa;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 700;
  color: var(--ion-color-primary);
}

.ios-share-icon {
  width: 18px;
  height: 18px;
  display: inline-block;
  vertical-align: middle;
  margin: 0 4px;
  filter: brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%);
}

/* PWA Benefits Section */
.pwa-benefits-section {
  padding: 60px 0 80px;
  background: #fafafa;
  margin: 60px 0 0;
}

.benefits-header {
  text-align: center;
  margin-bottom: 50px;
}

.benefits-header h3 {
  font-size: 2.2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 15px;
}

.benefits-header p {
  font-size: 1.1rem;
  color: #666;
  max-width: 500px;
  margin: 0 auto;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 50px;
}

.benefit-card {
  background: white;
  border-radius: 20px;
  padding: 40px 30px;
  text-align: center;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

.benefit-card:hover {
  transform: translateY(-5px);
  border-color: var(--ion-color-primary);
  box-shadow: 0 15px 40px rgba(235, 87, 99, 0.1);
}

.benefit-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, var(--ion-color-primary), var(--ion-color-primary-shade));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: white;
}

.benefit-card h4 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 15px;
}

.benefit-card p {
  color: #666;
  line-height: 1.6;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .benefits-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }
}

@media (max-width: 768px) {
  .section-title {
    font-size: 2.2rem;
  }
  
  .pwa-content {
    padding: 30px 20px;
  }
  
  .content-layout {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
  }
  
  .phone-frame {
    width: 280px;
    height: 560px;
  }
  
  .pwa-tabs {
    max-width: 100%;
  }
  
  .benefits-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    padding: 0 20px;
  }
  
  .instructions-container h3 {
    font-size: 1.6rem;
  }
  
  .step-content {
    margin-left: 0;
  }
  
  .step-header {
    justify-content: center;
  }
  
  .benefits-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .phone-frame {
    width: 240px;
    height: 480px;
  }
  
  .step-header {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }
  
  .step-content {
    margin-left: 0;
    text-align: center;
  }
}
</style> 